import { useState, useEffect } from "react";
import ProductCard from "./ProductCard";
import { getTrendingListProducts } from "../services/productService";

const IMG_BASE =
  import.meta.env.VITE_IMG_URL ?? "http://localhost:8000/storage";

const mapApiProduct = (p) => {
  const primary = p.images?.find((img) => img.is_primary) || p.images?.[0];
  const img1 = primary
    ? primary.image.startsWith("http")
      ? primary.image
      : `${IMG_BASE}/${primary.image}`
    : "/img/product/placeholder.jpg";
  const second = p.images?.length > 1 ? p.images[1] : primary;
  const img2 = second
    ? second.image.startsWith("http")
      ? second.image
      : `${IMG_BASE}/${second.image}`
    : img1;
  return {
    id: p.product_id,
    product_id: p.product_id,
    name: p.name,
    category: p.category,
    img1,
    img2,
  };
};

function useResponsiveVisible() {
  const [visible, setVisible] = useState(
    typeof window !== "undefined" ? getVisibleCount(window.innerWidth) : 5
  );
  useEffect(() => {
    const handleResize = () => setVisible(getVisibleCount(window.innerWidth));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return visible;
}

function getVisibleCount(width) {
  if (width < 480) return 1;
  if (width < 640) return 2;
  if (width < 768) return 3;
  if (width < 1024) return 4;
  return 5;
}

export default function TrendingListing() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const VISIBLE = useResponsiveVisible();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await getTrendingListProducts();
        const data = res.data?.data || res.data || [];
        setProducts(data.map(mapApiProduct));
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const maxIndex = Math.max(0, products.length - VISIBLE);

  useEffect(() => {
    if (index > maxIndex) setIndex(maxIndex);
  }, [maxIndex, index]);

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));

  if (loading) {
    return (
      <div className="py-12 bg-white flex justify-center items-center min-h-[200px]">
        <span className="animate-spin border-4 border-gray-200 border-l-primary rounded-full w-10 h-10 inline-block"></span>
      </div>
    );
  }

  if (!products.length) return null;

  return (
    <div className="py-10 sm:py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-8 sm:mb-10">
          <p className="text-sm italic text-[#43a047] font-serif tracking-wide mb-1">
            What's hot
          </p>
          <h2 className="text-2xl md:text-3xl font-medium text-[#112244] tracking-wide">
            Trending Products
          </h2>
        </div>

        <div className="relative flex items-center">
          <button
            onClick={prev}
            disabled={index === 0}
            className="hidden sm:flex absolute -left-2 md:-left-8 z-10 w-9 h-9 rounded-full bg-white shadow-md items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
            aria-label="Previous"
          >
            <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
              <path
                d="M9 1L1 9L9 17"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className="overflow-hidden w-full">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${index * (100 / VISIBLE)}%)` }}
            >
              {products.map((p) => (
                <div
                  key={p.product_id}
                  className="flex-shrink-0"
                  style={{ width: `${100 / VISIBLE}%` }}
                >
                  <div className="px-2">
                    <ProductCard product={p} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={next}
            disabled={index >= maxIndex}
            className="hidden sm:flex absolute -right-2 md:-right-8 z-10 w-9 h-9 rounded-full bg-white shadow-md items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20 transition-colors"
            aria-label="Next"
          >
            <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
              <path
                d="M1 1L9 9L1 17"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Mobile dots — one per possible starting position */}
        {maxIndex > 0 && (
          <div className="flex sm:hidden justify-center gap-2 mt-6">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === index ? "bg-[#43a047]" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}